import React, { useState, useRef, useCallback } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import styles from './Tooltip.module.css';
import { useResponsive } from '../../../../hooks';
import { doNothing } from '../../../../services/utils';

export const Tooltip = ({ children, content, tooltipClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const lastTouchTime = useRef(0);
  const { isMobileDevice } = useResponsive();

  const handleOpenChange = useCallback((shouldOpen) => {
    // Radix will handle the toggle in desktop
    if (!isMobileDevice) {
      setOpen(shouldOpen);
      return;
    }

    // Mobile will toggle on click only to avoid events conflicts from Radix
    if (!shouldOpen) {
      setOpen(false);
    }
  }, [isMobileDevice]);

  const handleTriggerClick = useCallback((event) => {
    if (isMobileDevice) {
      event.preventDefault();
      event.stopPropagation();

      // Simple debounce to prevent multiple toggles from touch events
      const now = Date.now();
      if (now - lastTouchTime.current < 300) return;
      lastTouchTime.current = now;

      setOpen((prev) => !prev);
    }
  }, [isMobileDevice]);

  const handleControntPointerDown = useCallback((e) => {
    if (isMobileDevice && e.pointerType === 'touch') {
      // Mobile devices should not trigger more events at this point
      doNothing();
    }
  }, [isMobileDevice]);

  const handlePointerDownOutside = useCallback((event) => {
    if (event.target.closest('[data-radix-tooltip-trigger]')) {
      event.preventDefault();
    }
  }, []);

  return (
    <RadixTooltip.Provider delayDuration={200}>
      <RadixTooltip.Root
        onOpenChange={handleOpenChange}
        open={open}
      >
        <RadixTooltip.Trigger
          asChild
          // Make sure mobile behavior works as expected
          onClick={handleTriggerClick}
          onPointerDown={handleControntPointerDown}
        >
          {children}
        </RadixTooltip.Trigger>

        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={`${styles.tooltip} ${tooltipClassName}`}
            onPointerDownOutside={handlePointerDownOutside}
            side="top"
            sideOffset={5}
          >
            <p className={styles.content}>{content}</p>
            <RadixTooltip.Arrow
              className={styles.tooltipArrow}
              height={5}
              width={10}
            />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
